<!--
  Super Admin: ThreadFlow's books (card #669, PR 5 of 5).

  ThreadFlow's OWN money for one period: revenue by type, billed versus
  collected, liabilities and pass-through, and a Stripe-versus-ledger check.
  Built ALONGSIDE the Money page, not in place of it (the replace-or-keep call
  is Jake's); /admin/money is untouched.

  Data: GET /superadmin/books/summary (read-only, no Stripe), the ledger CSV
  and POST /superadmin/books/check. Backend contract and category meanings:
  docs/domain/books.md. Categories are rendered exactly as the endpoint returns
  them; this page invents none.

  STRIPE IS NEVER CALLED ON PAGE LOAD. Only the "Run Stripe check" button POSTs.
  An INCOMPLETE check is not a pass: it gets its own chip and is never styled
  as clean.

  No design exists for this surface; it follows the payments pages.

  ASCII-only.
-->
<template>
    <div data-testid="sa-books">
        <h1 class="sa-page-title">Books</h1>
        <p class="sa-books-intro">
            ThreadFlow's own money for one period, from the order ledger and the
            platform invoices. Amounts are integer cents shown as dollars.
        </p>

        <!-- Header: period, mode, UTC note, CSV -->
        <div class="sa-books-header">
            <div class="sa-books-period" data-testid="sa-books-period">
                <button
                    type="button"
                    class="sa-books-btn"
                    data-testid="sa-books-period-prev"
                    aria-label="Previous period"
                    @click="step(-1)"
                >
                    &lt;
                </button>
                <select
                    v-model="kind"
                    class="sa-books-select"
                    data-testid="sa-books-period-kind"
                    aria-label="Period type"
                >
                    <option value="month">Month</option>
                    <option value="first">1st to 15th</option>
                    <option value="second">15th to month end</option>
                </select>
                <span class="sa-books-period-label" data-testid="sa-books-period-label">
                    {{ label }}
                </span>
                <button
                    type="button"
                    class="sa-books-btn"
                    data-testid="sa-books-period-next"
                    aria-label="Next period"
                    @click="step(1)"
                >
                    &gt;
                </button>
            </div>

            <label class="sa-books-mode">
                Mode
                <select
                    v-model="mode"
                    class="sa-books-select"
                    data-testid="sa-books-mode"
                >
                    <option value="live">Live</option>
                    <option value="sandbox">Sandbox</option>
                </select>
            </label>

            <span class="sa-books-utc" data-testid="sa-books-utc-note">Times are UTC</span>

            <button
                type="button"
                class="sa-books-btn sa-books-btn--primary"
                data-testid="sa-books-download-csv"
                :disabled="downloading"
                @click="downloadCsv"
            >
                {{ downloading ? "Preparing..." : "Download CSV" }}
            </button>
        </div>
        <p
            v-if="downloadError"
            class="sa-books-state sa-books-state--error"
            data-testid="sa-books-download-error"
        >
            {{ downloadError }}
        </p>
        <p class="sa-books-quiet">
            The CSV is the order ledger only (not invoices, subscriptions or Stripe
            costs), for the period and mode shown.
        </p>

        <p v-if="loading" class="sa-books-state" data-testid="sa-books-loading">Loading...</p>
        <p
            v-else-if="errorMsg"
            class="sa-books-state sa-books-state--error"
            data-testid="sa-books-error"
        >
            {{ errorMsg }}
        </p>

        <div v-else-if="data" data-testid="sa-books-settled">
            <p class="sa-books-context" data-testid="sa-books-context">
                {{ modeLabel }} - {{ label }} - recognized when booked
            </p>

            <p v-if="data.empty" class="sa-books-empty" data-testid="sa-books-empty">
                {{ data.hint || "Nothing was booked in this period for this mode." }}
                Distributors on Live: {{ data.distributors_by_mode.live }}; on Sandbox:
                {{ data.distributors_by_mode.sandbox }}.
            </p>

            <!-- Order ledger by category -->
            <section class="sa-books-section">
                <h2 class="sa-books-h2">Order ledger by category</h2>
                <table class="sa-table" data-testid="sa-books-summary-table">
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th class="num">Rows</th>
                            <th class="num">Booked</th>
                            <th class="num">Refunded</th>
                            <th class="num">Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="c in data.ledger.categories"
                            :key="c.category"
                            :data-testid="`sa-books-summary-row-${c.category}`"
                        >
                            <td>
                                {{ categoryLabel(c.category) }}
                                <span class="sa-books-kind">{{ kindLabel(c.kind) }}</span>
                            </td>
                            <td class="num">{{ c.rows }}</td>
                            <td class="num">{{ money(c.booked_cents) }}</td>
                            <td class="num">{{ money(c.refunded_cents) }}</td>
                            <td class="num">{{ money(c.net_cents) }}</td>
                        </tr>
                        <tr data-testid="sa-books-summary-row-unattributed">
                            <td>
                                No distributor on the order
                                <span class="sa-books-kind">shown in every mode</span>
                            </td>
                            <td class="num">{{ data.ledger.unattributed.rows }}</td>
                            <td class="num">{{ money(data.ledger.unattributed.booked_cents) }}</td>
                            <td class="num">{{ money(data.ledger.unattributed.refunded_cents) }}</td>
                            <td class="num">{{ money(data.ledger.unattributed.net_cents) }}</td>
                        </tr>
                        <tr
                            class="sa-books-total"
                            data-testid="sa-books-summary-row-threadflow_revenue"
                        >
                            <td>ThreadFlow revenue (order ledger)</td>
                            <td class="num"></td>
                            <td class="num">{{ money(data.threadflow_revenue.booked_cents) }}</td>
                            <td class="num">{{ money(data.threadflow_revenue.refunded_cents) }}</td>
                            <td class="num">{{ money(data.threadflow_revenue.net_cents) }}</td>
                        </tr>
                    </tbody>
                </table>
                <p class="sa-books-note" data-testid="sa-books-revenue-excludes">
                    ThreadFlow revenue above is the order ledger only. Not in revenue:
                    Net shipping and handling booked in the period
                    ({{ money(data.threadflow_revenue.excludes.unsplit_sh_net_cents) }};
                    carrier and margin are one ledger row, so none of it is counted) and
                    payroll shipping and handling billed
                    ({{ money(data.threadflow_revenue.excludes.payroll_sh_billed_cents) }};
                    it has no ledger row). Subscriptions and art services are separate
                    revenue, in the services block below. Revenue rows are not added
                    to pass-through or distributor cost rows.
                    <template v-if="data.threadflow_revenue.note">
                        {{ data.threadflow_revenue.note }}
                    </template>
                </p>
            </section>

            <!-- Services: subscriptions and art services -->
            <section class="sa-books-section">
                <h2 class="sa-books-h2">Services (additional revenue)</h2>
                <table class="sa-table" data-testid="sa-books-services-table">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th class="num">Earned</th>
                            <th class="num">Billed</th>
                            <th class="num">Collected</th>
                            <th class="num">Void</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="key in serviceKeys"
                            :key="key"
                            :data-testid="`sa-books-summary-row-${key}`"
                        >
                            <td>{{ categoryLabel(key) }}</td>
                            <td class="num">{{ money(data.services[key].earned_cents) }}</td>
                            <td class="num">{{ money(data.services[key].billed_cents) }}</td>
                            <td class="num">{{ money(data.services[key].collected_cents) }}</td>
                            <td class="num">{{ money(data.services[key].void_cents) }}</td>
                        </tr>
                    </tbody>
                </table>
                <p class="sa-books-note">
                    Earned is the service month and is reported for a month period only
                    (-- for a 1st/15th cycle). Billed is the finalized date and collected
                    the paid date, both in UTC.
                </p>
            </section>

            <!-- Distributor invoices -->
            <section class="sa-books-section">
                <h2 class="sa-books-h2">Distributor invoices (1st/15th)</h2>
                <p class="sa-books-quiet">
                    {{ data.distributor_invoices.invoices_billed }} billed,
                    {{ data.distributor_invoices.invoices_collected }} collected.
                    Collected means paid as of now, dated by issue, not by payment
                    ({{ data.distributor_invoices.collected_basis }}).
                </p>
                <table class="sa-table" data-testid="sa-books-invoices-table">
                    <thead>
                        <tr>
                            <th>Invoice line</th>
                            <th v-for="col in invoiceCols" :key="col.key" class="num">
                                {{ col.label }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="line in invoiceLines"
                            :key="line"
                            :data-testid="`sa-books-invoice-row-${line}`"
                        >
                            <td>{{ line === "total_cents" ? "Total" : categoryLabel(line) }}</td>
                            <td v-for="col in invoiceCols" :key="col.key" class="num">
                                {{ money(data.distributor_invoices[col.key][line]) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p class="sa-books-note">
                    Payroll shipping and handling billed:
                    {{ money(data.distributor_invoices.payroll_sh_billed_cents) }}.
                </p>
            </section>

            <!-- Card charges -->
            <section class="sa-books-section">
                <h2 class="sa-books-h2">Card charges</h2>
                <dl class="sa-books-dl" data-testid="sa-books-card-charges">
                    <dt>Charges</dt>
                    <dd>{{ data.card_charges.count }}</dd>
                    <dt>Charged</dt>
                    <dd>{{ money(data.card_charges.charged_cents) }}</dd>
                    <dt>Application fee requested</dt>
                    <dd>{{ money(data.card_charges.application_fee_requested_cents) }}</dd>
                    <dt>Application fee taken (where read)</dt>
                    <dd>{{ money(data.card_charges.application_fee_taken_cents) }}</dd>
                    <dt>Charges whose Stripe actuals were never read</dt>
                    <dd>{{ data.card_charges.actuals_unread_count }}</dd>
                </dl>
            </section>

            <!-- Liabilities and pass-through -->
            <section class="sa-books-section">
                <h2 class="sa-books-h2">Liabilities and pass-through</h2>
                <dl class="sa-books-dl" data-testid="sa-books-liabilities">
                    <dt>Owed to decorators, booked to date</dt>
                    <dd>{{ money(data.liabilities_at_period_end.decorator_booked_to_date_cents) }}</dd>
                    <dt>Paid out to date</dt>
                    <dd>{{ money(data.liabilities_at_period_end.decorator_paid_out_to_date_cents) }}</dd>
                    <dt>Decorator payable at period end</dt>
                    <dd>{{ money(data.liabilities_at_period_end.decorator_payable_cents) }}</dd>
                    <dt>Sales tax collected by ThreadFlow</dt>
                    <dd>{{ money(data.liabilities_at_period_end.sales_tax_collected_cents) }}</dd>
                    <dt>Payables approved in the period</dt>
                    <dd>
                        {{ data.decorator_payables.approved_count }}
                        ({{ money(data.decorator_payables.approved_cents) }})
                    </dd>
                    <dt>Payables transferred in the period</dt>
                    <dd>
                        {{ data.decorator_payables.transferred_count }}
                        ({{ money(data.decorator_payables.transferred_cents) }})
                    </dd>
                    <dt>Payables paid manually in the period</dt>
                    <dd>
                        {{ data.decorator_payables.paid_manually_count }}
                        ({{ money(data.decorator_payables.paid_manually_cents) }})
                    </dd>
                </dl>
                <p class="sa-books-note">{{ data.liabilities_at_period_end.sales_tax_note }}</p>
                <p class="sa-books-note">
                    Carrier pass-through and decorator pass-through are the pass-through
                    rows in the ledger table above. ThreadFlow's own Stripe and
                    Artistic Punch costs are not in these books yet.
                </p>
            </section>
        </div>

        <!-- Stripe check: stays out of the loading / error branches above so a
             summary failure never hides the control, and never runs on load. -->
        <section class="sa-books-section" data-testid="sa-books-check">
            <h2 class="sa-books-h2">Stripe check</h2>
            <p class="sa-books-quiet">
                Compares the ledger with what Stripe holds for this period and mode.
                It reads Stripe only when you press the button; large months can come
                back incomplete, so use the 1st/15th cycles for them.
            </p>
            <button
                type="button"
                class="sa-books-btn sa-books-btn--primary"
                data-testid="sa-books-check-run"
                :disabled="checking"
                @click="runCheck"
            >
                {{ checking ? "Checking..." : "Run Stripe check" }}
            </button>
            <p v-if="checking" class="sa-books-state" data-testid="sa-books-check-loading">
                Reading Stripe. This can take a minute.
            </p>
            <p
                v-if="checkError"
                class="sa-books-state sa-books-state--error"
                data-testid="sa-books-check-error"
            >
                {{ checkError }}
            </p>

            <div v-if="check" data-testid="sa-books-check-result">
                <p class="sa-books-check-head">
                    <span
                        :class="['sa-books-chip', `sa-books-chip--${checkChip}`]"
                        :data-status="checkChip"
                        data-testid="sa-books-check-status"
                    >
                        {{ statusLabel(checkChip) }}
                    </span>
                    <span data-testid="sa-books-check-ran-at">
                        Ran {{ check.ran_at_utc }} (UTC) for {{ check.mode }},
                        {{ check.period && check.period.key }}
                    </span>
                </p>
                <p
                    v-if="check.status === 'incomplete'"
                    class="sa-books-note sa-books-note--warn"
                    data-testid="sa-books-check-incomplete-note"
                >
                    Some of Stripe's lists were cut off or could not be read, so this
                    result is not a pass. Large months can come back incomplete: run the
                    check on the 1st/15th cycle periods instead.
                </p>
                <ul class="sa-books-classes" data-testid="sa-books-check-classes">
                    <li
                        v-for="(n, cls) in check.counts_by_class"
                        :key="cls"
                        :data-testid="`sa-books-check-class-${cls}`"
                    >
                        {{ cls }}: {{ n }}
                    </li>
                </ul>
                <p
                    v-if="check.has_unknown_environment"
                    class="sa-books-note"
                    data-testid="sa-books-check-unknown-env"
                >
                    {{ check.unknown_environment_count }} Stripe object(s) carry no
                    environment tag, so they cannot be matched to this environment. They
                    are listed apart and not counted as differences.
                </p>
                <p
                    v-if="check.unattributed_refunds_skipped"
                    class="sa-books-note"
                    data-testid="sa-books-check-unattributed-refunds"
                >
                    {{ check.unattributed_refunds_skipped }} refund(s) on orders with no
                    distributor were skipped because they have no mode.
                </p>

                <table
                    v-if="shownDifferences.length"
                    class="sa-table"
                    data-testid="sa-books-differences-table"
                >
                    <thead>
                        <tr>
                            <th>Class</th>
                            <th>Object</th>
                            <th class="num">Ledger</th>
                            <th class="num">Stripe</th>
                            <th class="num">Delta</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(d, i) in shownDifferences"
                            :key="i"
                            :data-testid="`sa-books-difference-${i + 1}`"
                        >
                            <td>{{ d.class }}</td>
                            <td>
                                <router-link v-if="diffLink(d)" :to="diffLink(d)">
                                    {{ d.object_type }} {{ d.object_id }}
                                </router-link>
                                <template v-else>
                                    {{ d.object_type }} {{ d.object_id || "" }}
                                </template>
                                <div v-if="d.stripe_id" class="sa-books-quiet">{{ d.stripe_id }}</div>
                            </td>
                            <td class="num">{{ money(d.ledger_cents) }}</td>
                            <td class="num">{{ money(d.stripe_cents) }}</td>
                            <td class="num">{{ money(d.delta_cents) }}</td>
                            <td>{{ d.note }}</td>
                        </tr>
                    </tbody>
                </table>
                <p
                    v-if="check.differences_truncated || hiddenCount > 0"
                    class="sa-books-note sa-books-note--warn"
                    data-testid="sa-books-differences-truncated"
                >
                    Showing {{ shownDifferences.length }} of {{ check.difference_count }}
                    differences. The list is capped; run the check on a 1st/15th cycle
                    to see fewer at a time.
                </p>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import api from "@/services/api";
import {
    currentUtcMonth,
    defaultBooksMode,
    formatBooksCents,
    periodKind,
    periodLabel,
    shiftPeriod,
    withKind,
} from "@/utils/booksPeriod";

// A multi-word component name (lint).
defineOptions({ name: "SuperAdminBooks" });

const SUMMARY_URL = "/superadmin/books/summary";
const CSV_URL = "/superadmin/books/ledger.csv";
const CHECK_URL = "/superadmin/books/check";
// The page renders at most this many differences, whatever the server sent.
const DIFFERENCE_DISPLAY_CAP = 200;

// Display names for the categories the endpoint returns. A category the page
// does not know is shown by its raw key, never dropped or renamed.
const CATEGORY_LABELS = {
    tf_revenue_platform_fee: "Platform fee",
    tf_revenue_decoration_margin: "Decoration margin",
    tf_revenue_sh_margin: "Shipping and handling margin",
    tf_revenue_subscription: "Subscriptions",
    tf_revenue_art_service: "Art services",
    passthrough_decorator: "Owed to decorators",
    passthrough_carrier: "Carrier pass-through",
    sh_unsplit: "Net shipping and handling (not split)",
    receivable_card_shortfall: "Card shortfall billed on invoice",
    distributor_cost: "Distributor's own cost",
    bookkeeping: "Bookkeeping markers",
    unclassified: "Unclassified",
};
const KIND_LABELS = {
    tf_revenue: "revenue",
    passthrough: "pass-through",
    unsplit: "not revenue, not pass-through",
    receivable: "funds rows above",
    distributor_cost: "not ThreadFlow money",
    bookkeeping: "bookkeeping",
    unclassified: "needs a rule",
};
const serviceKeys = ["tf_revenue_subscription", "tf_revenue_art_service"];
const invoiceLines = [
    "tf_revenue_platform_fee",
    "tf_revenue_decoration_margin",
    "passthrough_decorator",
    "sh_unsplit",
    "receivable_card_shortfall",
    "total_cents",
];
const invoiceCols = [
    { key: "billed", label: "Billed" },
    { key: "collected", label: "Collected" },
    { key: "outstanding", label: "Outstanding" },
    { key: "uncollectible", label: "Uncollectible" },
    { key: "void", label: "Void" },
];

const period = ref(currentUtcMonth());
const mode = ref(
    defaultBooksMode(typeof window !== "undefined" ? window.location.hostname : undefined),
);

const kind = computed({
    get: () => periodKind(period.value),
    set: (k) => {
        period.value = withKind(period.value, k);
    },
});
const label = computed(() => periodLabel(period.value));
const modeLabel = computed(() => (mode.value === "live" ? "Live" : "Sandbox"));

const data = ref(null);
const loading = ref(false);
const errorMsg = ref("");
let summarySeq = 0;

const downloading = ref(false);
const downloadError = ref("");

const check = ref(null);
const checking = ref(false);
const checkError = ref("");
let checkSeq = 0;
let downloadSeq = 0;

const shownDifferences = computed(() =>
    check.value ? check.value.differences.slice(0, DIFFERENCE_DISPLAY_CAP) : [],
);
const hiddenCount = computed(() =>
    check.value ? Math.max(0, check.value.differences.length - DIFFERENCE_DISPLAY_CAP) : 0,
);

const money = formatBooksCents;
const categoryLabel = (c) => CATEGORY_LABELS[c] || c;
const kindLabel = (k) => KIND_LABELS[k] || k;

// The backend reports "clean" whenever the differences list is empty, even
// when it also skipped refunds or found objects it could not place. That is
// not a full pass, so it gets its own chip, in words and not color alone.
const checkChip = computed(() => {
    const c = check.value;
    if (!c) return "";
    const caveats = c.has_unknown_environment || c.unattributed_refunds_skipped > 0;
    return c.status === "clean" && caveats ? "clean_with_caveats" : c.status;
});

function statusLabel(status) {
    if (status === "clean") return "Clean";
    if (status === "clean_with_caveats") return "Clean, with caveats - read the notes below";
    if (status === "differences") return "Differences found";
    if (status === "incomplete") return "Incomplete - not a pass";
    return status;
}

// Order drill-down for an order; the invoices page for platform invoices.
function diffLink(d) {
    // Digits only: the id comes from the API, but it is put into a URL path.
    if (d.object_type === "order" && /^[0-9]+$/.test(String(d.object_id ?? ""))) {
        return `/admin/payments/reconciliation/${d.object_id}`;
    }
    if (d.object_type === "distributor_invoice") return "/admin/distributor-invoices";
    return null;
}

// 401: the global auth response interceptor retries once, then logs out and
// redirects; this message is only a fallback, shown when it rejects without
// redirecting. 403: signed in without the settings capability.
function httpMessage(err, fallback) {
    const status = err?.response?.status;
    if (status === 401) return "Your session has expired. Sign in again.";
    if (status === 403) return "You do not have access to the books.";
    return fallback;
}

function step(delta) {
    period.value = shiftPeriod(period.value, delta);
}

async function loadSummary() {
    const seq = ++summarySeq;
    loading.value = true;
    errorMsg.value = "";
    try {
        const resp = await api.get(SUMMARY_URL, {
            params: { period: period.value, mode: mode.value },
        });
        if (seq !== summarySeq) return;
        data.value = resp.data;
    } catch (err) {
        if (seq !== summarySeq) return;
        data.value = null;
        errorMsg.value = httpMessage(
            err,
            "Could not load the books for this period. Try again.",
        );
    } finally {
        if (seq === summarySeq) loading.value = false;
    }
}

// A check result and a download message belong to the period and mode they
// were made for; neither survives a change of either.
watch([period, mode], () => {
    checkSeq += 1; // an in-flight check for the old selection is discarded
    checking.value = false;
    check.value = null;
    checkError.value = "";
    downloadSeq += 1; // a download still in flight is discarded
    downloading.value = false;
    downloadError.value = "";
    loadSummary();
});

onMounted(loadSummary);

function csvFilename(resp) {
    const cd = resp?.headers?.["content-disposition"] || "";
    const m = /filename="?([A-Za-z0-9._-]+)"?/.exec(cd);
    return m ? m[1] : `threadflow-books-ledger-${mode.value}-${period.value}.csv`;
}

async function downloadCsv() {
    const seq = ++downloadSeq;
    downloading.value = true;
    downloadError.value = "";
    try {
        // Through the shared axios instance so the bearer token is attached;
        // a plain link or window.open would send no Authorization header.
        const resp = await api.get(CSV_URL, {
            params: { period: period.value, mode: mode.value },
            responseType: "blob",
        });
        if (seq !== downloadSeq) return;
        const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = csvFilename(resp);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (err) {
        if (seq !== downloadSeq) return;
        const status = err?.response?.status;
        if (status === 413) {
            downloadError.value =
                "This period has too many ledger rows for one file. Choose a 1st/15th cycle and download each.";
        } else if (status === 429) {
            downloadError.value =
                "Export limit reached (10 downloads per hour). Try again later.";
        } else if (status === 401 || status === 403) {
            downloadError.value = httpMessage(err, "");
        } else {
            downloadError.value = "The download failed. Try again.";
        }
    } finally {
        if (seq === downloadSeq) downloading.value = false;
    }
}

async function runCheck() {
    const seq = ++checkSeq;
    checking.value = true;
    checkError.value = "";
    check.value = null;
    try {
        const resp = await api.post(CHECK_URL, { period: period.value, mode: mode.value });
        if (seq !== checkSeq) return;
        check.value = resp.data;
    } catch (err) {
        if (seq !== checkSeq) return;
        checkError.value =
            err?.response?.status === 429
                ? "Check limit reached (6 Stripe checks per hour). Try again later."
                : httpMessage(err, "The Stripe check failed. Nothing was changed; try again.");
    } finally {
        if (seq === checkSeq) checking.value = false;
    }
}
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #1a202c;
}
.sa-books-intro {
    font-size: 14px;
    color: #4a5568;
    margin: 0 0 16px;
    max-width: 760px;
}
.sa-books-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    margin-bottom: 8px;
}
.sa-books-period,
.sa-books-mode {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
}
.sa-books-period-label {
    font-weight: 600;
    min-width: 220px;
}
.sa-books-utc {
    font-size: 13px;
    color: var(--tf-fg-quiet);
}
.sa-books-select,
.sa-books-btn {
    font-size: 14px;
    padding: 4px 10px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
}
.sa-books-btn--primary {
    background: #2b6cb0;
    border-color: #2b6cb0;
    color: #fff;
}
.sa-books-btn:disabled {
    opacity: 0.6;
    cursor: default;
}
.sa-books-section {
    margin: 24px 0 28px;
}
.sa-books-h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #2d3748;
}
.sa-books-context {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    margin: 12px 0;
}
.sa-books-state {
    font-size: 14px;
    color: #4a5568;
    padding: 8px 0;
}
.sa-books-state--error {
    color: #c53030;
}
.sa-books-empty {
    font-size: 14px;
    background: #fffaf0;
    color: #744210;
    padding: 8px 12px;
    border-radius: 4px;
}
.sa-books-quiet {
    font-size: 13px;
    color: var(--tf-fg-quiet);
    margin: 4px 0 8px;
}
.sa-books-note {
    font-size: 13px;
    color: #4a5568;
    margin: 8px 0;
    max-width: 860px;
}
.sa-books-note--warn {
    color: #9c4221;
}
.sa-books-kind {
    margin-left: 6px;
    font-size: 12px;
    color: var(--tf-fg-quiet);
}
.sa-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.sa-table th,
.sa-table td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: top;
}
.sa-table .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
}
.sa-books-total td {
    font-weight: 600;
    border-top: 2px solid #a0aec0;
}
.sa-books-dl {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 4px 24px;
    font-size: 14px;
    margin: 0;
}
.sa-books-dl dt {
    color: #4a5568;
}
.sa-books-dl dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
}
.sa-books-check-head {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
}
.sa-books-chip {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 10px;
}
.sa-books-chip--clean {
    background: #f0fff4;
    color: #276749;
}
.sa-books-chip--clean_with_caveats {
    background: #fffaf0;
    color: #975a16;
}
.sa-books-chip--differences {
    background: #fff5f5;
    color: #c53030;
}
.sa-books-chip--incomplete {
    background: #fffaf0;
    color: #975a16;
}
.sa-books-classes {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    padding: 0;
    margin: 8px 0;
    font-size: 13px;
}
</style>
